import * as React from "react";
import { useRef, useState, useSyncExternalStore } from "react";
import styled, { css } from "styled-components";
import { Button, Table } from "components/bootstrap";
import copyToClipboard from "util/copyToClipboard";
import { telemetryDebugStore, isTelemetryDebugEnabled } from "logic/telemetry/debug/TelemetryDebugStore";
import TelemetryDebugEntryRow from "./TelemetryDebugEntryRow";
const Badge = styled.button(
  ({ theme }) => css`
    position: fixed;
    right: ${theme.spacings.md};
    bottom: ${theme.spacings.md};
    z-index: 1050;
    border: 1px solid ${theme.colors.cards.border};
    border-radius: ${theme.spacings.xs};
    background: ${theme.colors.global.contentBackground};
    color: ${theme.colors.text.primary};
    padding: ${theme.spacings.xs} ${theme.spacings.sm};
    font-size: ${theme.fonts.size.small};
    box-shadow: 0 2px 8px rgb(0 0 0 / 20%);
  `
);
const Drawer = styled.div(
  ({ theme }) => css`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1050;
    display: flex;
    flex-direction: column;
    background: ${theme.colors.global.contentBackground};
    border-top: 2px solid ${theme.colors.cards.border};
    box-shadow: 0 -2px 12px rgb(0 0 0 / 25%);
  `
);
const ResizeHandle = styled.div(
  ({ theme }) => css`
    height: 6px;
    flex-shrink: 0;
    cursor: ns-resize;
    touch-action: none;

    &:hover {
      background: ${theme.colors.cards.border};
    }
  `
);
const Toolbar = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.sm};
    padding: ${theme.spacings.xs} ${theme.spacings.sm};
    border-bottom: 1px solid ${theme.colors.cards.border};
  `
);
const Title = styled.strong(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.small};
    margin-right: ${theme.spacings.sm};
  `
);
const FilterInput = styled.input(
  ({ theme }) => css`
    flex: 1;
    max-width: 300px;
    padding: 2px ${theme.spacings.xs};
    border: 1px solid ${theme.colors.cards.border};
    border-radius: ${theme.spacings.xs};
    background: ${theme.colors.global.contentBackground};
    color: ${theme.colors.text.primary};
  `
);
const Body = styled.div(
  ({ theme }) => css`
    overflow-y: auto;
    padding: 0 ${theme.spacings.sm};
  `
);
const EmptyHint = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
    padding: ${theme.spacings.md};
    margin: 0;
  `
);
const Spacer = styled.div`
  flex: 1;
`;
const HEIGHT_STORAGE_KEY = "gl.telemetry-debug.height";
const DEFAULT_HEIGHT_PX = 400;
const MIN_HEIGHT_PX = 120;
const readStoredHeight = () => {
  try {
    const stored = Number(localStorage.getItem(HEIGHT_STORAGE_KEY));
    return stored > 0 ? stored : null;
  } catch {
    return null;
  }
};
const clampHeight = (height) => Math.min(Math.max(height, MIN_HEIGHT_PX), Math.round(window.innerHeight * 0.85));
const matchesFilter = (entry, filter) => {
  if (!filter) return true;
  const needle = filter.toLowerCase();
  return entry.eventType.toLowerCase().includes(needle) || JSON.stringify(entry.payload).toLowerCase().includes(needle);
};
const TelemetryDebugOverlay = () => {
  const enabled = useSyncExternalStore(telemetryDebugStore.subscribe, isTelemetryDebugEnabled);
  const liveEntries = useSyncExternalStore(telemetryDebugStore.subscribe, telemetryDebugStore.getEntries);
  const [open, setOpen] = useState(false);
  const [frozenEntries, setFrozenEntries] = useState(null);
  const [filter, setFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [height, setHeight] = useState(() => {
    var _a;
    return (_a = readStoredHeight()) != null ? _a : DEFAULT_HEIGHT_PX;
  });
  const dragStart = useRef(null);
  const handleDragStart = (e) => {
    var _a, _b;
    (_b = (_a = e.currentTarget).setPointerCapture) == null ? void 0 : _b.call(_a, e.pointerId);
    dragStart.current = { y: e.clientY, height };
  };
  const draggedHeight = (e) => clampHeight(dragStart.current.height + (dragStart.current.y - e.clientY));
  const handleDragMove = (e) => {
    if (!dragStart.current) return;
    setHeight(draggedHeight(e));
  };
  const handleDragEnd = (e) => {
    if (!dragStart.current) return;
    const finalHeight = draggedHeight(e);
    dragStart.current = null;
    setHeight(finalHeight);
    try {
      localStorage.setItem(HEIGHT_STORAGE_KEY, String(finalHeight));
    } catch {
    }
  };
  if (!enabled) return null;
  if (!open) {
    return /* @__PURE__ */ React.createElement(Badge, { type: "button", "aria-label": "Telemetry debug", onClick: () => setOpen(true) }, "Telemetry debug: ", liveEntries.length);
  }
  const entries = frozenEntries != null ? frozenEntries : liveEntries;
  const visible = entries.filter((entry) => matchesFilter(entry, filter)).reverse();
  const deltaFor = (entry) => {
    const index = entries.findIndex(({ id }) => id === entry.id);
    return index > 0 ? entry.timestamp - entries[index - 1].timestamp : null;
  };
  return /* @__PURE__ */ React.createElement(Drawer, { role: "region", "aria-label": "Telemetry debug panel", style: { height: `${height}px` } }, /* @__PURE__ */ React.createElement(
    ResizeHandle,
    {
      role: "separator",
      "aria-orientation": "horizontal",
      "aria-label": "Resize telemetry debug panel",
      onPointerDown: handleDragStart,
      onPointerMove: handleDragMove,
      onPointerUp: handleDragEnd
    }
  ), /* @__PURE__ */ React.createElement(Toolbar, null, /* @__PURE__ */ React.createElement(Title, null, "Telemetry Debug (", entries.length, ")"), /* @__PURE__ */ React.createElement(
    FilterInput,
    {
      type: "text",
      "aria-label": "Filter events",
      placeholder: "Filter by event name or payload...",
      value: filter,
      onChange: (e) => setFilter(e.target.value)
    }
  ), frozenEntries ? /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: () => setFrozenEntries(null) }, "Resume") : /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: () => setFrozenEntries(liveEntries) }, "Pause"), /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: () => telemetryDebugStore.clear() }, "Clear"), /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: () => copyToClipboard(JSON.stringify(entries, null, 2)) }, "Export session"), /* @__PURE__ */ React.createElement(Spacer, null), /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: () => setOpen(false) }, "Collapse")), /* @__PURE__ */ React.createElement(Body, null, visible.length === 0 ? /* @__PURE__ */ React.createElement(EmptyHint, null, "No telemetry events recorded yet \u2014 interact with the app to see them here.") : /* @__PURE__ */ React.createElement(Table, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Time"), /* @__PURE__ */ React.createElement("th", null, "Event"), /* @__PURE__ */ React.createElement("th", null, "Action"), /* @__PURE__ */ React.createElement("th", null, "Status"))), /* @__PURE__ */ React.createElement("tbody", null, visible.map((entry) => /* @__PURE__ */ React.createElement(
    TelemetryDebugEntryRow,
    {
      key: entry.id,
      entry,
      deltaMs: deltaFor(entry),
      expanded: expandedId === entry.id,
      onToggle: () => setExpandedId(expandedId === entry.id ? null : entry.id)
    }
  ))))));
};
export default TelemetryDebugOverlay;
